/**
 * Space Task Notification Event Helpers
 *
 * Shared notification event shapes for browser task mutations and
 * server-side Space task delivery.
 *
 * Primary responsibilities:
 * - Define the minimum task snapshot needed for notification decisions
 * - Normalize task status checks used by client and server paths
 *
 * Dependencies:
 * - Supabase generated task types
 */

import type { Tables } from '@/supabase/types'
import { normalizeTaskAssigneeIds } from '@/lib/spaces/task-assignees'

export type SpaceTaskNotificationSnapshot = Pick<
  Tables<'space_tasks'>,
  | 'id'
  | 'space_id'
  | 'title'
  | 'status'
  | 'assigned_to'
  | 'assigned_by'
  | 'folder_id'
  | 'parent_task_id'
  | 'priority'
  | 'sprint_id'
  | 'start_date'
  | 'tags'
  | 'watchers'
  | 'custom_fields'
  | 'due_date'
  | 'user_id'
  | 'updated_at'
  | 'completed_at'
  | 'archived_at'
>

export interface SpaceTaskNotificationEvent {
  before?: SpaceTaskNotificationSnapshot | null
  after: SpaceTaskNotificationSnapshot
}

export function buildSpaceTaskNotificationSnapshot(
  task: Partial<SpaceTaskNotificationSnapshot> &
    Pick<
      SpaceTaskNotificationSnapshot,
      'id' | 'space_id' | 'title' | 'user_id'
    >
) {
  return {
    id: task.id,
    space_id: task.space_id,
    title: task.title,
    status: task.status ?? null,
    assigned_to: task.assigned_to ?? null,
    assigned_by: task.assigned_by ?? null,
    folder_id: task.folder_id ?? null,
    parent_task_id: task.parent_task_id ?? null,
    priority: task.priority ?? null,
    sprint_id: task.sprint_id ?? null,
    start_date: task.start_date ?? null,
    tags: task.tags ?? null,
    watchers: task.watchers ?? null,
    custom_fields: task.custom_fields ?? null,
    due_date: task.due_date ?? null,
    user_id: task.user_id,
    updated_at: task.updated_at ?? null,
    completed_at: task.completed_at ?? null,
    archived_at: task.archived_at ?? null
  } satisfies SpaceTaskNotificationSnapshot
}

export function getSpaceTaskNotificationAssigneeIds(
  task: Pick<SpaceTaskNotificationSnapshot, 'assigned_to' | 'custom_fields'>
) {
  const customAssigneeIds =
    task.custom_fields &&
    typeof task.custom_fields === 'object' &&
    !Array.isArray(task.custom_fields) &&
    'assignee_ids' in task.custom_fields
      ? task.custom_fields.assignee_ids
      : undefined

  return normalizeTaskAssigneeIds(customAssigneeIds, task.assigned_to)
}

export function normalizeSpaceTaskStatus(value: string | null | undefined) {
  return typeof value === 'string' ? value.trim().toLowerCase() : ''
}

export function isCompletedSpaceTaskStatus(
  value: string | null | undefined
) {
  const status = normalizeSpaceTaskStatus(value)
  return status === 'done' || status === 'completed'
}

export function isInactiveSpaceTaskStatus(
  value: string | null | undefined
) {
  const status = normalizeSpaceTaskStatus(value)
  return (
    status === 'cancelled' ||
    status === 'done' ||
    status === 'completed'
  )
}
